import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import React from "react";
import { type Mode } from "~/features/ui/get-theme-style";
import { CtaButtonPair, type CtaPairLink } from "./cta-button-pair";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type CallToActionCardBlockViewProps = {
  anchor?: {
    id?: string | null;
    name?: string | null;
  } | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  mode?: string | null;
  primaryLink?: CtaPairLink | null;
  secondaryLink?: CtaPairLink | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};

/**
 * UI 3.0 "Call To Action Card" module. A single bordered card holding optional
 * copy (title + text) on the left and the solid/outline CTA button pair on the
 * right — horizontal on desktop, stacked on mobile. When the copy is omitted the
 * buttons align to the end. Light/dark follows the section's `data-theme`.
 */
export function CallToActionCardBlockView({
  anchor,
  hasTopDivider = true,
  headline,
  mode = "light",
  primaryLink,
  secondaryLink,
  sectionProps,
  text,
  theme = "white"
}: CallToActionCardBlockViewProps) {
  const hasCopy = Boolean(headline || text);
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={anchor?.id || undefined} data-anchor-name={anchor?.name || undefined} $_css={[
  // `relative` makes the opaque section background cover the page
  // background pattern, which otherwise paints above static sections.
  {
    "position": "relative",
    "paddingLeft": "var(--spacing-lg)",
    "paddingRight": "var(--spacing-lg)",
    "paddingTop": "var(--spacing-3xl)",
    "paddingBottom": "var(--spacing-3xl)",
    "@media (min-width: 1024px)": {
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)"
    }
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }]}>
      <_StyledDiv $_css2={[{
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "center",
      "gap": "var(--spacing-xl)",
      "@media (min-width: 1024px)": {
        "flexDirection": "row",
        "justifyContent": "center"
      }
    }, hasCopy && {
      "@media (min-width: 1024px)": {
        "justifyContent": "space-between"
      }
    }]}>
        {hasCopy && <_StyledDiv2 $_css3={[{
        "display": "flex",
        "flexDirection": "column",
        "alignItems": "flex-start",
        "gap": "var(--spacing-md)",
        "@media (min-width: 1024px)": {
          "flex": "1 1 0%"
        }
      }]}>
            {headline && <_StyledH $_css4={[{
          "fontSize": "2.2rem",
          "lineHeight": "1.2",
          "fontWeight": "400",
          "color": "var(--text-primary)",
          "@media (min-width: 1024px)": {
            "fontSize": "3.2rem",
            "lineHeight": "1.2",
            "letterSpacing": "0.032rem",
            "fontWeight": "400"
          }
        }]}>{headline}</_StyledH>}
            {text && <_StyledP $_css5={[{
          "fontSize": "1.6rem",
          "lineHeight": "1.5",
          "fontWeight": "400",
          "color": "var(--text-primary)",
          "@media (min-width: 1024px)": {
            "fontSize": "1.8rem",
            "lineHeight": "1.5",
            "letterSpacing": "0.018rem",
            "fontWeight": "400"
          }
        }]}>{text}</_StyledP>}
          </_StyledDiv2>}

        <CtaButtonPair primary={primaryLink} secondary={secondaryLink} />
      </_StyledDiv>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-dmmgkb-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-dmmgkb-1"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-dmmgkb-2"
})`${p => p.$_css3}`;
var _StyledH = _styled4("h2").withConfig({
  componentId: "sc-sf-dmmgkb-3"
})`${p => p.$_css4}`;
var _StyledP = /*#__PURE__*/_styled5("p").withConfig({
  displayName: "call-to-action-card___StyledP",
  componentId: "sc-dswkan-0"
})(["", ""], p => p.$_css5);